import type {
  DeleteContractItemMutationOptions,
  DeleteContractItemMutationVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysContract } from '../queryKeys';

import { deleteContractItem } from './deleteContractItem';

/**
 * Custom hook for soft deleting a line item from a contract. The deletion is
 * persisted immediately, which the success toast tells the user.
 *
 * @param options - Additional mutation options; callbacks run after the hook's own.
 * @returns The mutation; `isPending` stays true until the contract and its item lists have refetched.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/delete_contract_item_contracts__contract_id__items__item_id__delete
 */
export const useMutationDeleteContractItem = (
  options?: DeleteContractItemMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async ({
      contractId,
      itemId,
    }: DeleteContractItemMutationVariables) =>
      deleteContractItem(contractId, itemId),
    async onSettled(data, error, variables, onMutateResult, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.getItems(variables.contractId, undefined),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.get(variables.contractId),
        }),
        // Only marked stale: refetching the deleted item while its page is
        // still open would answer 404 and toast an error.
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.getItem(
            variables.contractId,
            variables.itemId,
          ),
          refetchType: 'none',
        }),
        options?.onSettled?.(data, error, variables, onMutateResult, context),
      ]);
    },
    async onSuccess(data, variables, onMutateResult, context) {
      ToastService.success(
        i18n.t(($) => $.contractItem.deleteContractItem.success, {
          ns: 'data',
        }),
      );

      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
